import { Fragment, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Boxes, ChevronDown, ChevronRight, HardHat, Hammer, Truck, Wrench } from "lucide-react";
import { api } from "@/lib/api";
import { EmptyState } from "@/components/EmptyState";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { fmtNum } from "@/lib/format";

const TYPE = {
  MATERIAL: { label: "Material", Icon: Boxes },
  LABOUR: { label: "Labour", Icon: HardHat },
  SUBCONTRACT: { label: "Subcontract", Icon: Hammer },
  PLANT: { label: "Equipment", Icon: Truck },
  OTHER: { label: "Other", Icon: Wrench },
};
const PHASE_LABEL = { ENGINEERING: "Engineering", PROCUREMENT: "Procurement", CONSTRUCTION: "Construction" };

/** Financial years (April–March): the current one and the two before it, plus "All time". */
function yearOptions() {
  const now = new Date();
  const start = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const opts = [{ value: "all", label: "All time" }];
  for (let y = start; y > start - 3; y--) {
    opts.push({ value: String(y), label: `Apr ${y} – Mar ${y + 1}`, from: `${y}-04-01`, to: `${y + 1}-03-31` });
  }
  return opts;
}

const qty = (v, uom) => (v === null || v === undefined ? "—" : `${fmtNum(v, 2)}${uom ? ` ${uom}` : ""}`);
const varClass = (v) => (Number(v) < 0 ? "bg-rose-500/15 text-rose-400" : "bg-emerald-500/15 text-emerald-400");

/** Quantity still to order: red when more has been ordered than planned. "—" for cost types that are not bought on an order. */
function ToOrder({ type }) {
  if (!type || type.to_order_qty === null || type.to_order_qty === undefined) return <span className="text-muted-foreground">—</span>;
  const n = Number(type.to_order_qty);
  return <span className={n < 0 ? "font-semibold text-rose-400" : n === 0 ? "text-emerald-400" : ""} data-testid="to-order-qty">{qty(n, type.planned_uom || type.ordered_uom)}</span>;
}

function VariancePill({ value }) {
  return <span className={`inline-block rounded-md px-2 py-0.5 font-mono text-xs font-semibold ${varClass(value)}`}>{fmtNum(value, 0)}</span>;
}

function ProgressBar({ pct }) {
  const n = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <div className="flex items-center gap-2">
      <span className="w-9 text-right font-mono text-[11px] text-muted-foreground">{fmtNum(n, 0)}%</span>
      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-[#910029]" style={{ width: `${n}%` }} /></div>
    </div>
  );
}

/** Plan workspace: one row per activity that opens into its cost types (material, labour, subcontract, equipment, other).
 *  Quantities and rupees side by side; the activity row adds up its cost types. */
export function CostTypeTracking({ projectId, refreshKey }) {
  const years = useMemo(yearOptions, []);
  const [year, setYear] = useState("all");
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState({});

  useEffect(() => {
    let live = true;
    const y = years.find((o) => o.value === year);
    const params = y && y.from ? { date_from: y.from, date_to: y.to } : {};
    api.get(`/projects/${projectId}/cost-tracking`, { params })
      .then((r) => { if (live) { setData(r.data); setFailed(false); } })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [projectId, refreshKey, year, years]);

  const toggle = (key) => setOpen((o) => ({ ...o, [key]: !o[key] }));
  const sections = useMemo(() => {
    if (!data) return [];
    const order = ["ENGINEERING", "PROCUREMENT", "CONSTRUCTION", null];
    return order.map((phase) => ({ phase, rows: data.rows.filter((r) => (r.phase || null) === phase) })).filter((s) => s.rows.length);
  }, [data]);
  const allKeys = data ? data.rows.map((r) => r.wbs_item_id || "none") : [];
  const allOpen = allKeys.length > 0 && allKeys.every((k) => open[k]);

  return (
    <div className="rounded-xl border border-border bg-card" data-testid="cost-type-tracking">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border p-4">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Plan workspace — planned vs actual by cost type</h2>
          <p className="mt-1 max-w-3xl text-xs text-muted-foreground">
            Planned comes from the approved estimate. Actual quantity comes from goods receipts (material), certified JMRs (subcontract),
            site head-count (labour, in head-days) and equipment hours. A different unit is flagged, not converted.
            The date range limits the actual side only.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" className="text-xs text-muted-foreground underline-offset-2 hover:underline" data-testid="cost-type-toggle-all"
            onClick={() => setOpen(Object.fromEntries(allKeys.map((k) => [k, !allOpen])))}>{allOpen ? "Collapse all" : "Expand all"}</button>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="h-8 w-[190px] text-xs" data-testid="cost-type-year"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {failed ? (
        <EmptyState title="Could not load" description="Reload the page to try again." testId="cost-type-error" />
      ) : !data ? null : data.rows.length === 0 ? (
        <EmptyState title="Nothing to compare yet" description="Approve an estimate, or record receipts, labour or equipment hours." testId="cost-type-empty" />
      ) : (
        <div className="space-y-4 p-4">
          {!data.has_approved_budget && (
            <p className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-300" data-testid="cost-type-no-budget">
              No approved estimate yet, so only actuals are shown.
            </p>
          )}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-testid="cost-type-totals">
            {data.by_type.map((t) => (
              <div key={t.type} className="rounded-lg border border-border p-3">
                <div className="text-xs font-semibold text-muted-foreground">{TYPE[t.type]?.label || t.type}</div>
                <div className="mt-1 font-mono text-xs text-foreground">Planned {fmtNum(t.planned_amount, 0)}</div>
                {t.ordered_amount !== null && t.ordered_amount !== undefined && (
                  <div className="font-mono text-xs text-foreground" data-testid={`cost-type-ordered-${t.type}`}>Ordered {fmtNum(t.ordered_amount, 0)} · to order <b className={Number(t.to_order_amount) < 0 ? "text-rose-400" : ""}>{fmtNum(t.to_order_amount, 0)}</b></div>
                )}
                <div className="font-mono text-xs text-foreground">Actual {fmtNum(t.actual_amount, 0)}</div>
                <div className="mt-1 flex items-center gap-1.5 text-[11px] text-muted-foreground">Remaining <VariancePill value={t.remaining_amount} /></div>
              </div>
            ))}
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">No.</TableHead><TableHead>Activity / Work package</TableHead><TableHead>Type</TableHead>
                <TableHead className="text-right">Planned qty</TableHead><TableHead className="text-right">Ordered qty</TableHead>
                <TableHead className="text-right">Left to order</TableHead><TableHead className="text-right">Actual qty</TableHead>
                <TableHead className="text-right">Planned ₹</TableHead><TableHead className="text-right">Actual ₹</TableHead>
                <TableHead className="text-right">Remaining ₹</TableHead><TableHead>% Complete</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sections.map((s) => (
                <Fragment key={s.phase || "none"}>
                  <TableRow className="bg-muted/40" data-testid={`cost-type-phase-${s.phase || "none"}`}>
                    <TableCell colSpan={11} className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {s.phase ? PHASE_LABEL[s.phase] || s.phase : "Not assigned to an activity"}
                      {s.phase && data.by_phase.find((p) => p.phase === s.phase) && (
                        <span className="ml-3 font-mono normal-case tracking-normal">
                          planned {fmtNum(data.by_phase.find((p) => p.phase === s.phase).planned_amount, 0)} · actual {fmtNum(data.by_phase.find((p) => p.phase === s.phase).actual_amount, 0)}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                  {s.rows.map((row) => {
                    const key = row.wbs_item_id || "none";
                    const expanded = !!open[key];
                    const single = row.types.length === 1 ? row.types[0] : null;
                    return (
                      <Fragment key={key}>
                        <TableRow className="cursor-pointer hover:bg-muted/30" onClick={() => toggle(key)} data-testid={`cost-type-activity-${key}`}>
                          <TableCell className="font-mono text-xs text-muted-foreground">
                            <span className="inline-flex items-center gap-1">{expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}{row.code || ""}</span>
                          </TableCell>
                          <TableCell className="font-semibold text-foreground">{row.name}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{single ? TYPE[single.type]?.label : `${row.types.length} types`}</TableCell>
                          <TableCell className="text-right font-mono text-xs">{single ? qty(single.planned_qty, single.planned_uom) : qty(row.scope_qty, row.scope_uom)}</TableCell>
                          <TableCell className="text-right font-mono text-xs">{single && single.ordered_qty !== null ? qty(single.ordered_qty, single.ordered_uom) : "—"}</TableCell>
                          <TableCell className="text-right font-mono text-xs">{single ? <ToOrder type={single} /> : "—"}</TableCell>
                          <TableCell className="text-right font-mono text-xs">{single ? qty(single.actual_qty, single.actual_uom) : "—"}</TableCell>
                          <TableCell className="text-right font-mono text-xs font-semibold">{fmtNum(row.planned_amount, 2)}</TableCell>
                          <TableCell className="text-right font-mono text-xs font-semibold">{fmtNum(row.actual_amount, 2)}</TableCell>
                          <TableCell className="text-right"><VariancePill value={row.remaining_amount} /></TableCell>
                          <TableCell><ProgressBar pct={row.pct_complete} /></TableCell>
                        </TableRow>
                        {expanded && row.types.map((t) => {
                          const Icon = TYPE[t.type]?.Icon || Wrench;
                          return (
                            <TableRow key={t.type} className="bg-muted/10" data-testid={`cost-type-row-${key}-${t.type}`}>
                              <TableCell />
                              <TableCell className="pl-8 text-foreground"><span className="inline-flex items-center gap-2"><Icon className="h-3.5 w-3.5 text-muted-foreground" />{TYPE[t.type]?.label || t.type}</span></TableCell>
                              <TableCell className="text-xs text-muted-foreground">{TYPE[t.type]?.label || t.type}</TableCell>
                              <TableCell className="text-right font-mono text-xs">{qty(t.planned_qty, t.planned_uom)}</TableCell>
                              <TableCell className="text-right font-mono text-xs">{t.ordered_qty !== null ? qty(t.ordered_qty, t.ordered_uom) : "—"}</TableCell>
                              <TableCell className="text-right font-mono text-xs"><ToOrder type={t} /></TableCell>
                              <TableCell className="text-right font-mono text-xs">
                                {qty(t.actual_qty, t.actual_uom)}
                                {t.unit_warning && <span title={t.unit_warning} className="ml-1 inline-block align-middle text-amber-400" data-testid="unit-warning"><AlertTriangle className="h-3.5 w-3.5" /></span>}
                              </TableCell>
                              <TableCell className="text-right font-mono text-xs">{fmtNum(t.planned_amount, 2)}</TableCell>
                              <TableCell className="text-right font-mono text-xs">{fmtNum(t.actual_amount, 2)}</TableCell>
                              <TableCell className="text-right"><VariancePill value={t.remaining_amount} /></TableCell>
                              <TableCell><ProgressBar pct={t.pct_complete} /></TableCell>
                            </TableRow>
                          );
                        })}
                        {expanded && row.labour_by_trade.length > 0 && (
                          <TableRow className="bg-muted/10">
                            <TableCell />
                            <TableCell colSpan={10} className="pl-8 text-xs text-muted-foreground" data-testid={`labour-trades-${key}`}>
                              Labour by trade (head-days): {row.labour_by_trade.map((l) => `${l.trade} ${fmtNum(l.head_days, 1)}`).join(" · ")}
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
